// src/hooks/useRolePermissions.js
import { useEffect, useMemo, useState } from "react";
import { db } from "../lib/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { normalizeRole } from "./useUserRole";

function roleToPermissionDocId(role) {
  const r = normalizeRole(role);

  if (r === "super user") return "Super User";
  if (r === "admin") return "Admin";
  if (r === "supervisor") return "Supervisor";
  if (r === "field user") return "Field User";
  if (r === "manager") return "Manager";

  return String(role || "").trim();
}

/**
 * Live-subscribes to role-based permissions.
 * @param {string} role
 * @returns {{ perms: object|null, loading: boolean, error: string|null, can: function, roleDocId: string }}
 */
export default function useRolePermissions(role) {
  const [perms, setPerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const roleDocId = useMemo(() => roleToPermissionDocId(role), [role]);

  useEffect(() => {
    if (!roleDocId) {
      setPerms(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const ref = doc(db, "role_permissions", roleDocId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        console.log("[useRolePermissions] raw role:", role);
        console.log("[useRolePermissions] doc id:", roleDocId);
        console.log("[useRolePermissions] perms:", data);
        setPerms(data);
        setLoading(false);
      },
      (err) => {
        console.error("[useRolePermissions] subscribe failed:", err);
        setError(err?.message || String(err));
        setPerms({});
        setLoading(false);
      }
    );

    return () => unsub();
  }, [role, roleDocId]);

  const can = useMemo(() => {
    return (key) => Boolean(perms && perms[key]);
  }, [perms]);

  return { perms, loading, error, can, roleDocId };
}